import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import classNames from 'classnames';
import { CheckCircle } from '@material-ui/icons';
import Typography from '@material-ui/core/Typography';
import MetaControl from 'Components/UI/Meta/MetaControl';
import useMobileLayout from 'Hooks/useMobileLayout';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import ProgressSlider from '../GoalProgressSlider';
import Edit from '../../../../../../Icons/Edit';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.common.white,
    width: '100%',
  },
  topRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'top',
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(6),
  },
  progressBarRow: {
    padding: theme.spacing(0, 4),
  },
  progressBar: {
    display: 'flex',
    background: `${theme.palette.facet.lightGray2} 0% 0% no-repeat padding-box`,
    height: '5px',
    marginRight: theme.spacing(1),
  },
  boldLabel: {
    '& label': {
      color: theme.palette.facet.primaryGray,
      fontWeight: theme.typography.fontWeightBold,
      whiteSpace: 'nowrap',
    },
  },
  largeValueField: {
    margin: theme.spacing(3),
    '& h5': {
      color: theme.palette.facet.accentGreen,
      fontSize: theme.typography.h1.fontSize,
      fontWeight: theme.typography.fontWeightRegular,
    },
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  editButton: {
    backgroundColor: theme.palette.facet.accentBlue,
    color: theme.palette.common.white,
    borderRadius: '24px',
    '&:hover': {
      backgroundColor: theme.palette.facet.accentBlue2,
    },
    boxShadow: 'unset',
  },
  description: {
    color: theme.palette.facet.primaryGray,
  },
  descriptionContainer: {
    margin: theme.spacing(0, 2),
    marginBottom: theme.spacing(8),
  },
  descriptionLabel: {
    color: theme.palette.facet.primaryGray,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.body1.fontSize,
  },
  completeButtonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  completeButton: {
    backgroundColor: theme.palette.facet.lightGray2,
    color: theme.palette.common.white,
    borderRadius: '24px',
    '&:hover': {
      backgroundColor: theme.palette.facet.primaryGray,
    },
    boxShadow: 'unset',
  },
  completeButtonCompleted: {
    backgroundColor: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  [theme.breakpoints.down('md')]: {
    largeValueField: {
      '& h5': {
        fontSize: theme.typography.h2.fontSize,
      },
    },
  },
  [theme.breakpoints.down('sm')]: {
    topRow: {
      marginBottom: theme.spacing(4),
      marginRight: 'unset',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
    largeValueField: {
      margin: theme.spacing(1),
      width: '50%',
    },
    mobileRow: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
    },
    progressBarRow: {
      padding: theme.spacing(0, 3),
    },
  },
}));

export default function ReadOnlyGoalForm({ onEdit, goalResource, onMarkCompleted }) {
  const {
    rootNodeProps,
    logResourceEvent,
    logResourceEventSuccess,
    logResourceEventFailureWithError,
  } = useEventTrackingLogEvent({ nodeName: 'Goal Display' });
  const classes = useStyles();
  const isMobile = useMobileLayout();

  const progress = +goalResource.attr('percentComplete');
  const completed = goalResource.attr('completed') === 'true';

  const largeValueStyles = classNames(classes.largeValueField, classes.boldLabel);

  const toggleMarkCompleted = async () => {
    try {
      await goalResource.attr('completed', !completed).submit();
      logResourceEventSuccess('Client Goals Page: Clicked Button to toggle Complete on Goal', goalResource, {
        completed: !completed,
      });
      onMarkCompleted();
    } catch (err) {
      displayErrorMessage(err, err.errors);
      logResourceEventFailureWithError('Client Goals Page: Clicked Button to toggle Complete on Goal', goalResource, err, {
        completed: !completed,
      });
    }
  };

  const handleClickEdit = () => {
    onEdit();
    logResourceEvent('Client Goals Page: Clicked Edit Button on Goal', goalResource);
  };


  return (
    <div {...rootNodeProps} className={classes.root}>
      <div className={classes.topRow}>
        {
          isMobile ? (
            <>
              <div className={classes.mobileRow}>
                <MetaControl attrName="amount" resource={goalResource} className={largeValueStyles} />
                <MetaControl attrName="priority" resource={goalResource} className={largeValueStyles} />
              </div>
              <div className={classes.mobileRow}>
                <MetaControl attrName="endDate" resource={goalResource} className={largeValueStyles} />
                <MetaControl attrName="amountSaved" resource={goalResource} className={largeValueStyles} />
              </div>
            </>
          ) : (
            <>
              <MetaControl attrName="amount" resource={goalResource} className={largeValueStyles} />
              <MetaControl attrName="endDate" resource={goalResource} className={largeValueStyles} />
              <MetaControl attrName="priority" resource={goalResource} className={largeValueStyles} />
              <MetaControl attrName="amountSaved" resource={goalResource} className={largeValueStyles} />
              <div className={classes.buttonContainer}>
                <Button
                  className={classes.editButton}
                  side="medium"
                  variant="contained"
                  onClick={handleClickEdit}
                  startIcon={<Edit />}
                >
                    Edit
                </Button>
              </div>
            </>
          )
        }
      </div>
      <div className={classes.progressBarRow}>
        <ProgressSlider
          value={progress}
        />
        <div className={classes.completeButtonRow}>
          <Button
            className={classNames(classes.completeButton, {
              [classes.completeButtonCompleted]: completed,
            })}
            side="medium"
            variant="contained"
            startIcon={completed && <CheckCircle />}
            onClick={toggleMarkCompleted}
          >
            {completed ? 'Complete' : 'Mark Complete'}
          </Button>
        </div>
      </div>
      <div className={classes.descriptionContainer}>
        <Typography
          variant="h5"
          className={classes.descriptionLabel}
        >
          Description
        </Typography>
        <RichTextDisplay
          content={goalResource.attr('description')}
          className={classes.description}
        />
      </div>
    </div>
  );
}

ReadOnlyGoalForm.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onMarkCompleted: PropTypes.func.isRequired,
  goalResource: PropTypes.func.isRequired,
};
